/**
 * Multi-Location KPI Breakdown Builder — iter423
 *
 * Builds the same `breakdown` payload shape consumed by
 * `StockAuditBreakdownDialog` (used by the single-location Calculate
 * panel), but with rows representing PER-LOCATION contributions
 * instead of per-product. This is what powers the "show me how this
 * KPI was calculated" drill-down on the new Multi-Location Calculate
 * & Results tab.
 *
 * Every KPI surfaced in the panel has a corresponding case here so
 * the user can click any tile and see (a) the academic formula,
 * (b) the substituted-value worked example, and (c) the per-location
 * rows that summed to the consolidated/subset total. Online and
 * offline produce identical breakdowns because the inputs (each
 * location's stored snapshot) are the same.
 */

const fmtCurrency = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v));
};
const fmtPct = (v) => v == null ? '—' : `${Number(v).toFixed(2)}%`;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Produce a per-location row collection for the given numeric field
 * on each location's calculation snapshot, optionally negating the
 * value (for outflows that should be shown as negatives in the
 * breakdown). Rows include a worked-example column showing the
 * substituted formula.
 */
function rowsForField(visibleLocations, field, { negate = false, label = null, signSymbol = null } = {}) {
  return visibleLocations.map((loc) => {
    const v = Number(loc.calculation?.[field] ?? 0);
    return {
      location_name: loc.location_name,
      location_type: loc.location_type,
      auditor_name: loc.auditor_name || '—',
      sign: signSymbol || (negate ? '−' : '+'),
      value_label: label,
      value: negate ? -v : v,
    };
  }).filter(r => r.value !== 0 || (visibleLocations.length <= 5));  // keep zero rows for small subsets
}

const baseCols = (valLabel = 'Value') => [
  { key: 'location_name', label: 'Location', align: 'left' },
  { key: 'auditor_name', label: 'Auditor', align: 'left' },
  { key: 'sign', label: '', align: 'center' },
  { key: 'value', label: valLabel, align: 'right', fmt: fmtCurrency },
];

/**
 * Compose a worked-example string substituting each location's
 * contribution into the consolidated formula. E.g.:
 *   "= 4,500.00 (Cellar) + 1,200.00 (Main Bar) = 5,700.00"
 */
function workedExample(visibleLocations, field, totalValue, { negate = false } = {}) {
  const parts = visibleLocations
    .filter(l => l.calculation)
    .map((l) => {
      const v = Number(l.calculation[field] ?? 0);
      return { name: l.location_name, value: negate ? -v : v };
    });
  if (parts.length === 0) return null;
  const formula = parts.map((p, i) => {
    const abs = Math.abs(p.value).toFixed(2);
    const sign = i === 0 ? (p.value < 0 ? '−' : '') : (p.value < 0 ? '− ' : '+ ');
    return `${sign}£${abs} (${p.name})`;
  }).join(' ');
  return `${formula} = £${Number(totalValue || 0).toFixed(2)}`;
}

export function buildMultiLocBreakdown(kpiKey, { visibleLocations, rollup, mode, hasCellar, productRollup, productRollupLoading, salesBreakdown, selectedLocationIds }) {
  const v = visibleLocations || [];
  const r = rollup || {};
  const isConsolidated = mode === 'consolidated';
  const scopeLabel = isConsolidated ? 'consolidated' : 'selection';

  // ── Per-product rows (iter425) ─────────────────────────────────
  // When productRollup is available we build a per-product table for
  // most stock-flow KPIs. The same selection filter (consolidated vs
  // a subset of locations) is applied so the rows reconcile with the
  // headline figure.
  const fieldForKpi = {
    opening: 'opening_value',
    deliveries: 'deliveries_value',
    credits: 'credits_value',
    sales_at_cost: 'sales_value',
    cos: 'sales_value',
    transfers_in: 'transfers_in_value',
    transfers_out: 'transfers_out_value',
    management: 'management_value',
    allowance: 'allowance_value',
    expected_closing: 'expected_value',
    actual_closing: 'counted_value',
    closing_stock: 'counted_value',
  };
  const qtyFieldForKpi = {
    opening: 'opening_qty',
    deliveries: 'deliveries_qty',
    credits: 'credits_qty',
    sales_at_cost: 'sales_qty',
    cos: 'sales_qty',
    transfers_in: 'transfers_in_qty',
    transfers_out: 'transfers_out_qty',
    management: 'management_qty',
    allowance: 'allowance_qty',
    actual_closing: 'counted_qty',
    closing_stock: 'counted_qty',
  };

  const productRows = (() => {
    if (!productRollup?.products) return null;
    const valueField = fieldForKpi[kpiKey];

    // ── Synthetic per-product values ──────────────────────────
    // Some KPIs are compositional and don't have a single bucket.
    // We compute them per-product from the existing rollup buckets so
    // the By-Product drill-down reconciles with the headline figure.
    //
    //   cos_indirect (Consumption at Cost) =
    //     opening + deliveries + transfers_in
    //     − credits − transfers_out + management − counted
    //   (mirrors the formula in kpi_engine.py & stock_audit_engine.py)
    const SYNTHETIC = {
      cos_indirect: (locData) => {
        const opening = Number(locData.opening_value || 0);
        const deliveries = Number(locData.deliveries_value || 0);
        const credits = Number(locData.credits_value || 0);
        const tin = Number(locData.transfers_in_value || 0);
        const tout = Number(locData.transfers_out_value || 0);
        const mgmt = Number(locData.management_value || 0);
        const counted = Number(locData.counted_value || 0);
        return opening + deliveries + tin - credits - tout + mgmt - counted;
      },
    };

    if (!valueField && !SYNTHETIC[kpiKey]) return null;

    const qtyField = qtyFieldForKpi[kpiKey] || null;
    const useSubset = !isConsolidated && Array.isArray(selectedLocationIds) && selectedLocationIds.length > 0;
    const visibleSessionIdSet = new Set(useSubset ? selectedLocationIds : []);

    // iter440 — EXPLODE: one row per (product, location). This lets the
    // dialog group by subcategory AND filter by location chip, matching
    // the rich UX of single-location drill-downs while still letting
    // users see per-child contribution at a glance.
    const rows = [];
    for (const p of (productRollup.products || [])) {
      const byLoc = p.by_location || {};
      for (const [loc_name, locData] of Object.entries(byLoc)) {
        if (useSubset && !visibleSessionIdSet.has(locData.session_id)) continue;
        let value = 0;
        let qty = 0;
        if (SYNTHETIC[kpiKey]) {
          value = SYNTHETIC[kpiKey](locData);
        } else {
          const bucketKey = valueField.replace('_value', '');
          value = Number(locData[`${bucketKey}_value`] ?? 0);
          if (qtyField) {
            const qtyBucketKey = qtyField.replace('_qty', '');
            qty = Number(locData[`${qtyBucketKey}_qty`] ?? 0);
          }
        }
        if (value === 0 && qty === 0) continue;
        rows.push({
          product_id: p.product_id,
          product_name: p.product_name || `Product ${p.product_id}`,
          product_code: p.product_code || '',
          category: p.category || '(uncategorised)',
          subcategory: p.subcategory || '',
          group_label: (p.subcategory && String(p.subcategory).trim())
            ? String(p.subcategory).trim()
            : (p.category || '(uncategorised)'),
          stock_unit: p.stock_unit || '',
          location_name: loc_name,
          qty,
          value,
        });
      }
    }
    rows.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    return rows;
  })();

  // iter440 — Sales-side per-product breakdown for Estimated Receipts.
  // Uses the dedicated /consolidated-breakdown payload (one flat_row per
  // product × location) which carries selling-price, VAT, and category
  // context not present in the cost-side products-rollup.
  const salesProductView = (() => {
    if (kpiKey !== 'estimated_receipts') return null;
    if (!salesBreakdown) return null;
    const allRows = salesBreakdown.flat_rows || [];
    const useSubset = !isConsolidated && Array.isArray(selectedLocationIds) && selectedLocationIds.length > 0;
    const visibleSessionIdSet = new Set(useSubset ? selectedLocationIds : []);
    const sessionByLoc = {};
    for (const l of (salesBreakdown.locations || [])) {
      if (l.session_id != null) sessionByLoc[l.location_name] = l.session_id;
    }
    const filtered = allRows.filter(r => {
      if (!useSubset) return true;
      const sid = sessionByLoc[r.location_name];
      return sid == null || visibleSessionIdSet.has(sid);
    });
    const rows = filtered.map(r => {
      const groupLabel = (r.subcategory && String(r.subcategory).trim())
        ? String(r.subcategory).trim()
        : (r.category || '(uncategorised)');
      const valueIncl = r.value_incl_vat != null ? r.value_incl_vat : r.value_excl_vat;
      const priceIncl = r.selling_price_incl_vat != null ? r.selling_price_incl_vat : r.selling_price_excl_vat;
      // Strip the "— LocationName" suffix the backend added so the
      // product name column remains clean — the location is its own
      // column / filter chip now.
      const baseName = (r.product_name || '').split(' — ').slice(0, -1).join(' — ') || r.product_name;
      return {
        product_name: baseName,
        location_name: r.location_name || '(unknown)',
        category: r.category || '(uncategorised)',
        subcategory: r.subcategory || '',
        group_label: groupLabel,
        stock_unit: r.stock_unit || '',
        qty: r.consumption_qty,
        price_incl: priceIncl,
        vat_rate: r.vat_rate,
        value: valueIncl,
      };
    }).sort((a, b) => Math.abs(b.value || 0) - Math.abs(a.value || 0));
    const total = rows.reduce((s, x) => s + (x.value || 0), 0);
    return {
      key: 'by-product',
      label: 'By Product',
      columns: [
        { key: 'product_name', label: 'Product', align: 'left' },
        { key: 'location_name', label: 'Location', align: 'left' },
        { key: 'qty', label: 'Consumption qty', align: 'right', fmt: (val) => val == null ? '—' : Number(val).toLocaleString('en-GB', { maximumFractionDigits: 4 }) },
        { key: 'stock_unit', label: 'Unit', align: 'left' },
        { key: 'price_incl', label: 'Sell/unit (incl VAT)', align: 'right', fmt: fmtCurrency },
        { key: 'vat_rate', label: 'VAT %', align: 'right', fmt: (val) => val == null ? '—' : `${Number(val).toFixed(0)}%` },
        { key: 'value', label: 'Value (incl VAT)', align: 'right', fmt: fmtCurrency },
      ],
      rows,
      searchable: true,
      searchKeys: ['product_name', 'category', 'subcategory', 'location_name'],
      filterFields: [
        { key: 'category', label: 'Category' },
        { key: 'subcategory', label: 'Subcategory' },
        { key: 'location_name', label: 'Location' },
      ],
      groupBy: 'group_label',
      exportFilenameBase: 'parent-estimated-receipts-breakdown',
      emptyMessage: 'No Estimated Receipts contributions in the current selection.',
      totals: { label: 'Total Estimated Receipts (incl VAT)', value: total, fmt: fmtCurrency },
    };
  })();

  // iter425.1 — Always render the By-Product tab when a productRollup is
  // loaded so the tab affordance is stable across KPIs.
  const productView = (() => {
    // Sales-side KPI gets its dedicated rich view from salesBreakdown.
    if (salesProductView) return salesProductView;

    // iter425.2 — While the rollup fetch is in-flight, show a loading
    // tab so the user sees the By-Product affordance immediately on
    // dialog open instead of having tabs pop in after several seconds.
    if (!productRollup?.products) {
      if (productRollupLoading) {
        return {
          key: 'by-product',
          label: 'By Product',
          columns: [{ key: 'product_name', label: 'Product', align: 'left' }],
          rows: [],
          emptyMessage: 'Loading per-product breakdown…',
        };
      }
      return null;
    }

    if (productRows && productRows.length > 0) {
      const total = productRows.reduce((s, x) => s + (x.value || 0), 0);
      return {
        key: 'by-product',
        label: 'By Product',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'location_name', label: 'Location', align: 'left' },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'qty', label: 'Qty', align: 'right', fmt: (val) => val == null || val === 0 ? '—' : Number(val).toLocaleString('en-GB', { maximumFractionDigits: 4 }) },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCurrency },
        ],
        rows: productRows,
        searchable: true,
        searchKeys: ['product_name', 'product_code', 'category', 'subcategory', 'location_name'],
        filterFields: [
          { key: 'category', label: 'Category' },
          { key: 'subcategory', label: 'Subcategory' },
          { key: 'location_name', label: 'Location' },
        ],
        groupBy: 'group_label',
        exportFilenameBase: `parent-${kpiKey}-breakdown`,
        totals: { label: 'Total', value: total, fmt: fmtCurrency },
      };
    }

    // Empty state — KPI is aggregate-only (e.g. sales-side Inc-VAT KPIs)
    // or every product contributed zero. The dialog still gets a tab so
    // E2E tests and users can rely on a consistent UI shape.
    const aggregateOnly = ['accounts_sales', 'result',
                           'yield_pct', 'gp', 'cos_pct', 'stockholding'].includes(kpiKey);
    return {
      key: 'by-product',
      label: 'By Product',
      columns: [
        { key: 'product_name', label: 'Product', align: 'left' },
        { key: 'value', label: 'Value', align: 'right', fmt: fmtCurrency },
      ],
      rows: [],
      emptyMessage: aggregateOnly
        ? 'This KPI is calculated at the location level (Inc-VAT receipts, yield %, GP %, etc.) and does not have a per-product decomposition. See the "By Location" tab for the full breakdown.'
        : 'No per-product contributions for this KPI in the current selection.',
    };
  })();

  // ── Switch produces the per-LOCATION breakdown (default view).
  // After the switch, we wrap the result so the dialog can also offer
  // a By-Product view via `views` when productRows are available.
  const buildLocationView = () => {
    switch (kpiKey) {
    case 'opening': {
      return {
        title: 'Opening Stock — by location',
        formula: 'Opening Stock = Σ (location opening_stock_value)',
        explanation: `Each ${scopeLabel === 'consolidated' ? 'location in this audit' : 'location in your current selection'} contributes its own opening stock. Inter-location transfers do not affect opening stock — they only affect the period between counts.`,
        example: workedExample(v, 'opening_stock_value', r.opening_stock_value),
        columns: baseCols('Opening'),
        rows: rowsForField(v, 'opening_stock_value', { signSymbol: '+' }),
        totals: { label: 'Total Opening Stock', value: r.opening_stock_value, fmt: fmtCurrency },
      };
    }
    case 'deliveries': {
      return {
        title: 'Deliveries — by location',
        formula: 'Deliveries = Σ (location deliveries_value)',
        explanation: `Σ of posted deliveries within the audit period across each ${scopeLabel === 'consolidated' ? 'location' : 'selected location'}. Draft deliveries are excluded by the engine.`,
        example: workedExample(v, 'deliveries_value', r.deliveries_value),
        columns: baseCols('Deliveries'),
        rows: rowsForField(v, 'deliveries_value', { signSymbol: '+' }),
        totals: { label: 'Total Deliveries', value: r.deliveries_value, fmt: fmtCurrency },
      };
    }
    case 'credits': {
      return {
        title: 'Credits — by location',
        formula: 'Credits = Σ (location credits_value)',
        explanation: 'Returns to supplier reduce stock value but do NOT change running WAC. Posted credits only.',
        example: workedExample(v, 'credits_value', r.credits_value),
        columns: baseCols('Credits'),
        rows: rowsForField(v, 'credits_value', { signSymbol: '−' }),
        totals: { label: 'Total Credits', value: r.credits_value, fmt: fmtCurrency },
      };
    }
    case 'cos':
    case 'sales_at_cost': {
      return {
        title: 'Cost of Sales (PLU) — by location',
        formula: 'Cost of Sales (PLU) = Σ (location sales_at_cost)',
        explanation: 'PLU-derived cost: sales lines valued at WAC at the time of sale. Diagnostic under revised methodology; drives GP under legacy.',
        example: workedExample(v, 'sales_at_cost', r.sales_at_cost),
        columns: baseCols('COS (PLU)'),
        rows: rowsForField(v, 'sales_at_cost', { signSymbol: '−' }),
        totals: { label: 'Total Cost of Sales', value: r.sales_at_cost, fmt: fmtCurrency },
      };
    }
    case 'cos_indirect': {
      const op = r.opening_stock_value || 0;
      const de = r.deliveries_value || 0;
      const ti = r.transfers_in_total || 0;
      const cr = r.credits_value || 0;
      const to = r.transfers_out_total || 0;
      const mg = r.management_cost_of_sales || r.management_value || 0;
      const ac = r.total_value || 0;
      return {
        title: 'Consumption at Cost — by location',
        formula: 'Consumption at Cost = Opening + Deliveries + Transfers In − Credits − Transfers Out + Management − Closing',
        explanation: `Stock-movement derived cost (what actually moved out). For consolidated views inter-location transfers between selected locations net to zero. ${hasCellar ? 'Cellar issues to outlets contribute as outflows on the cellar side and inflows on the outlet side.' : ''}`,
        example: `£${op.toFixed(2)} + £${de.toFixed(2)} + £${ti.toFixed(2)} − £${cr.toFixed(2)} − £${to.toFixed(2)} + £${mg.toFixed(2)} − £${ac.toFixed(2)} = £${Number(r.cost_of_sales_indirect || 0).toFixed(2)}`,
        columns: baseCols('Consumption'),
        rows: v.filter(l => l.calculation).map(loc => {
          const c = loc.calculation;
          const val = round2(
            (c.opening_stock_value || 0)
            + (c.deliveries_value || 0)
            + (c.transfers_in_value || 0)
            - (c.credits_value || 0)
            - (c.transfers_out_value || 0)
            + (c.management_value || 0)
            - (c.total_value || 0)
          );
          return {
            location_name: loc.location_name,
            location_type: loc.location_type,
            auditor_name: loc.auditor_name || '—',
            sign: val < 0 ? '−' : '+',
            value: val,
          };
        }),
        totals: { label: 'Total Consumption at Cost', value: r.cost_of_sales_indirect, fmt: fmtCurrency },
      };
    }
    case 'transfers_in': {
      return {
        title: 'Transfers In — by location',
        formula: 'Transfers In = Σ (location transfers_in_value)',
        explanation: 'Stock received from another internal location (cellar → bar, etc.). Posted transfers only.',
        example: workedExample(v, 'transfers_in_value', r.transfers_in_total),
        columns: baseCols('Transfers In'),
        rows: rowsForField(v, 'transfers_in_value', { signSymbol: '+' }),
        totals: { label: 'Total Transfers In', value: r.transfers_in_total, fmt: fmtCurrency },
      };
    }
    case 'transfers_out': {
      return {
        title: 'Transfers Out — by location',
        formula: 'Transfers Out = Σ (location transfers_out_value)',
        explanation: 'Stock issued to another internal location. Posted transfers only.',
        example: workedExample(v, 'transfers_out_value', r.transfers_out_total),
        columns: baseCols('Transfers Out'),
        rows: rowsForField(v, 'transfers_out_value', { signSymbol: '−' }),
        totals: { label: 'Total Transfers Out', value: r.transfers_out_total, fmt: fmtCurrency },
      };
    }
    case 'management': {
      return {
        title: 'Management Lines — by location',
        formula: 'Management = Σ (location management_value)',
        explanation: 'Staff drinks, complimentary, breakages flagged as management. Flows through expected closing.',
        example: workedExample(v, 'management_value', r.management_value),
        columns: baseCols('Management'),
        rows: rowsForField(v, 'management_value', { signSymbol: '+' }),
        totals: { label: 'Total Management', value: r.management_value, fmt: fmtCurrency },
      };
    }
    case 'allowance': {
      return {
        title: 'Allowances — by location',
        formula: 'Allowance = Σ (location allowance_value)',
        explanation: 'Promotions, tastings, marketing. Excluded from Cost of Sales; affects only the Adjusted Result.',
        example: workedExample(v, 'allowance_value', r.allowance_value),
        columns: baseCols('Allowance'),
        rows: rowsForField(v, 'allowance_value', { signSymbol: '−' }),
        totals: { label: 'Total Allowance', value: r.allowance_value, fmt: fmtCurrency },
      };
    }
    case 'expected_closing': {
      return {
        title: 'Expected Closing — by location',
        formula: 'Expected Closing = Σ (Opening + Deliveries + Transfers In − Credits − Effective Outflow + Management)',
        explanation: 'Predicted closing stock from movement. Effective Outflow = transfers_out for cellars, sales_at_cost for outlets.',
        example: workedExample(v, 'expected_closing_value', r.expected_closing_value),
        columns: baseCols('Expected'),
        rows: rowsForField(v, 'expected_closing_value', { signSymbol: '+' }),
        totals: { label: 'Total Expected Closing', value: r.expected_closing_value, fmt: fmtCurrency },
      };
    }
    case 'actual_closing':
    case 'closing_stock': {
      return {
        title: 'Closing Stock — by location',
        formula: 'Closing Stock = Σ (location total_value)',
        explanation: 'Σ of counted stock at the end of period, valued at WAC.',
        example: workedExample(v, 'total_value', r.total_value),
        columns: baseCols('Closing'),
        rows: rowsForField(v, 'total_value', { signSymbol: '+' }),
        totals: { label: 'Total Closing Stock', value: r.total_value, fmt: fmtCurrency },
      };
    }
    case 'accounts_sales': {
      return {
        title: 'Actual Receipts (incl VAT) — by location',
        formula: 'Actual Receipts = Σ (location accounts_sales_incl_vat)',
        explanation: 'Till receipts entered on each location\'s Sales step. Cellars do not contribute (no sales).',
        example: workedExample(v, 'accounts_sales_incl_vat', r.accounts_sales_incl_vat),
        columns: baseCols('Actual Receipts'),
        rows: rowsForField(v, 'accounts_sales_incl_vat', { signSymbol: '+' }),
        totals: { label: 'Total Actual Receipts', value: r.accounts_sales_incl_vat, fmt: fmtCurrency },
      };
    }
    case 'estimated_receipts': {
      return {
        title: 'Estimated Receipts (incl VAT) — by location',
        formula: 'Estimated Receipts = Σ (consumption × selling price incl VAT)',
        explanation: 'Predicted sales income from observed stock consumption. Σ across each outlet.',
        example: workedExample(v, 'estimated_receipts_incl_vat', r.estimated_receipts_incl_vat),
        columns: baseCols('Est. Receipts'),
        rows: rowsForField(v, 'estimated_receipts_incl_vat', { signSymbol: '+' }),
        totals: { label: 'Total Estimated Receipts', value: r.estimated_receipts_incl_vat, fmt: fmtCurrency },
      };
    }
    case 'result':
    case 'stocktake_result': {
      const isRevised = r.headline_methodology === 'revised';
      const headline = r.headline_stocktake_result ?? r.stocktake_result;
      const formula = isRevised
        ? 'Result = Actual Receipts − Estimated Receipts + Allowances − Management(inc VAT)'
        : 'Result = Actual Closing − Expected Closing + Allowances − Management(inc VAT) + Included Wastage';
      const worked = isRevised
        ? `£${Number(r.accounts_sales_incl_vat || 0).toFixed(2)} − £${Number(r.estimated_receipts_incl_vat || 0).toFixed(2)} + £${Number(r.allowance_value || 0).toFixed(2)} − £${Number(r.management_sales_incl_vat || 0).toFixed(2)} = £${Number(headline || 0).toFixed(2)}`
        : `£${Number(r.total_value || 0).toFixed(2)} − £${Number(r.expected_closing_value || 0).toFixed(2)} + £${Number(r.allowance_value || 0).toFixed(2)} − £${Number(r.management_sales_incl_vat || 0).toFixed(2)} = £${Number(headline || 0).toFixed(2)}`;
      return {
        title: 'Stock Audit Result — by location',
        formula,
        explanation: `Headline methodology: ${isRevised ? 'Revised (Actual Receipts vs Estimated Receipts)' : 'Legacy (Actual vs Expected Closing)'}. Each location's adjusted result is rolled up.`,
        example: worked,
        columns: baseCols('Result'),
        rows: v.filter(l => l.calculation).map(loc => ({
          location_name: loc.location_name,
          location_type: loc.location_type,
          auditor_name: loc.auditor_name || '—',
          sign: (loc.calculation.stocktake_result || 0) < 0 ? '−' : '+',
          value: loc.calculation.stocktake_result_legacy_adjusted ?? loc.calculation.stocktake_result,
        })),
        totals: { label: 'Headline Stocktake Result', value: headline, fmt: fmtCurrency },
      };
    }
    case 'gp':
    case 'gross_profit': {
      const gp = r.headline_gross_profit;
      const cos = r.cost_of_sales_indirect;
      const rev = r.accounts_sales_excl_vat ?? r.sales_at_selling_excl_vat;
      return {
        title: 'Gross Profit — calculation',
        formula: 'Gross Profit = Revenue (excl VAT) − Cost of Sales',
        explanation: `Under revised methodology: Actual Receipts excl VAT − Consumption at Cost. Under legacy: PLU revenue − PLU COS. Aggregated across ${mode === 'consolidated' ? 'all calculated locations' : 'the selected locations'}.`,
        example: rev != null && cos != null
          ? `£${Number(rev).toFixed(2)} − £${Number(cos).toFixed(2)} = £${Number(gp || 0).toFixed(2)}`
          : null,
        columns: [
          { key: 'metric', label: 'Metric', align: 'left' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCurrency },
        ],
        rows: [
          { metric: 'Revenue (excl VAT)', value: rev },
          { metric: 'Cost of Sales', value: -(cos || 0) },
        ],
        totals: { label: 'Gross Profit', value: gp, fmt: fmtCurrency },
      };
    }
    case 'gp_pct': {
      const gp = r.headline_gross_profit;
      const rev = r.accounts_sales_excl_vat ?? r.sales_at_selling_excl_vat;
      const pct = r.headline_gp_pct;
      return {
        title: 'GP % — calculation',
        formula: 'GP % = (Gross Profit ÷ Revenue excl VAT) × 100',
        explanation: 'Margin earned on every £ of revenue.',
        example: gp != null && rev != null
          ? `(£${Number(gp).toFixed(2)} ÷ £${Number(rev).toFixed(2)}) × 100 = ${fmtPct(pct)}`
          : null,
        columns: [
          { key: 'metric', label: 'Metric', align: 'left' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCurrency },
        ],
        rows: [
          { metric: 'Gross Profit', value: gp },
          { metric: 'Revenue (excl VAT)', value: rev },
        ],
        totals: { label: 'GP %', value: pct != null ? `${pct.toFixed(2)}%` : '—', fmt: (x) => x },
      };
    }
    case 'cos_pct': {
      const cos = r.cost_of_sales_indirect;
      const rev = r.accounts_sales_excl_vat ?? r.sales_at_selling_excl_vat;
      const pct = rev > 0 ? (cos / rev) * 100 : null;
      return {
        title: 'COS % — calculation',
        formula: 'COS % = (Cost of Sales ÷ Revenue excl VAT) × 100',
        explanation: 'Share of every £ of revenue absorbed by cost of stock consumed.',
        example: cos != null && rev != null
          ? `(£${Number(cos).toFixed(2)} ÷ £${Number(rev).toFixed(2)}) × 100 = ${fmtPct(pct)}`
          : null,
        columns: [
          { key: 'metric', label: 'Metric', align: 'left' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCurrency },
        ],
        rows: [
          { metric: 'Cost of Sales (Consumption at Cost)', value: cos },
          { metric: 'Revenue (excl VAT)', value: rev },
        ],
        totals: { label: 'COS %', value: pct != null ? `${pct.toFixed(2)}%` : '—', fmt: (x) => x },
      };
    }
    case 'yield_pct': {
      const acc = r.accounts_sales_incl_vat;
      const est = r.estimated_receipts_incl_vat;
      const mgmt = r.management_sales_incl_vat;
      const yp = r.yield_pct ?? ((est + mgmt) > 0 ? ((acc / (est + mgmt)) * 100) : null);
      return {
        title: 'Yield % — calculation',
        formula: 'Yield % = Actual Receipts ÷ (Estimated Receipts + Management) × 100',
        explanation: 'Operational benchmark. 100% = takings perfectly match what stock movement predicts.',
        example: acc != null && est != null
          ? `£${Number(acc).toFixed(2)} ÷ (£${Number(est).toFixed(2)} + £${Number(mgmt || 0).toFixed(2)}) × 100 = ${fmtPct(yp)}`
          : null,
        columns: [
          { key: 'metric', label: 'Metric', align: 'left' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCurrency },
        ],
        rows: [
          { metric: 'Actual Receipts (incl VAT)', value: acc },
          { metric: 'Estimated Receipts (incl VAT)', value: est },
          { metric: 'Management (incl VAT)', value: mgmt },
        ],
        totals: { label: 'Yield %', value: yp != null ? `${yp.toFixed(2)}%` : '—', fmt: (x) => x },
      };
    }
    case 'stockholding': {
      const days = r.stockholding_days;
      const avg = r.avg_daily_cos;
      const closing = r.total_value;
      const periodDays = r.period_days;
      return {
        title: 'Stockholding — calculation',
        formula: 'Stockholding (days) = Closing Stock ÷ Avg Daily Cost of Sales',
        explanation: 'How many days of stock you\'re holding at current burn-rate.',
        example: closing != null && avg != null
          ? `£${Number(closing).toFixed(2)} ÷ £${Number(avg).toFixed(2)}/day = ${days ?? '—'} days`
          : null,
        columns: [
          { key: 'metric', label: 'Metric', align: 'left' },
          { key: 'value', label: 'Value', align: 'right', fmt: (x) => typeof x === 'number' ? fmtCurrency(x) : x },
        ],
        rows: [
          { metric: 'Closing Stock', value: closing },
          { metric: 'Period days', value: periodDays != null ? `${periodDays} days` : '—' },
          { metric: 'Cost of Sales (consumption)', value: r.cost_of_sales_indirect },
          { metric: 'Avg daily COS', value: avg },
        ],
        totals: { label: 'Stockholding', value: days != null ? `${days} days` : '—', fmt: (x) => x },
      };
    }
    default:
      return {
        title: kpiKey,
        formula: '—',
        explanation: 'Detailed breakdown not available for this metric yet.',
        columns: baseCols(),
        rows: [],
      };
    }
  };

  const locationView = buildLocationView();
  if (!productView) return locationView;
  // Combined: tabbed views — preserve top-level title/formula/explanation/example
  // for the dialog header. The dialog reads `views` and renders a tab strip.
  return {
    title: locationView.title,
    formula: locationView.formula,
    explanation: locationView.explanation,
    example: locationView.example,
    views: [
      {
        key: 'by-location',
        label: 'By Location',
        columns: locationView.columns,
        rows: locationView.rows,
        totals: locationView.totals,
        figures: locationView.figures,
      },
      productView,
    ],
    // Fallback fields so the dialog still works if it ignores `views`.
    columns: locationView.columns,
    rows: locationView.rows,
    totals: locationView.totals,
    figures: locationView.figures,
  };
}
